import React, { useState } from 'react';

import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import { Dialog } from '../../../components/layouts';
import { Currency } from '../../../core/api/general/general.types';
import { MenuItem, NutritionMenuItem } from '../../../core/api/menu/menu.types';
import { QuantityControlType } from '../../../core/types/basket.types';
import {
  formatNumberToCurrency,
  getBasketItemTotal,
} from '../../../utils/basketUtils';
import { MenuDialogAddControls, MenuDialogItemDetails } from '../components';
import { MenuItemDetails } from '../components/MenuOptionsDialog/components/MenuItemDetails';
import { useAllergens } from '../hooks/useAllergens';

type Props = {
  currency: Currency;
  isNutritionInfoError?: boolean;
  item: MenuItem;
  languageCode: string;
  menuItemNutritionInfo?: NutritionMenuItem;
  menuItemOptionSetItemNutritionInfos: NutritionMenuItem[];
  onAddItem: QuantityControlType['onAddItem'];
  restaurantPhoneNumber: string;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  showDialog: boolean;
  showNutritionInfo: boolean;
  translate: any;
};

const useStyles = makeStyles((theme: Theme) => ({
  dialog: {
    '& .MuiDialogTitle-root': {
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
    '& .MuiDialogContent-root': {
      padding: 0,
      overflowX: 'hidden',
    },
    '& .fd-text__h2': {
      margin: 0,
    },
  },

  dialogActions: {
    display: 'flex',
    flexFlow: 'row nowrap',
    width: '100%',
  },

  [theme.breakpoints.up('sm')]: {
    dialog: {
      '& .MuiDialog-paperScrollPaper': {
        height: 'calc(100vh - 48px)', // - 48px = 24px padding top and bottom
        maxHeight: '720px',
      },
    },
  },
}));

export function MenuDetailDialog(props: Props): JSX.Element {
  const {
    currency,
    isNutritionInfoError = false,
    item,
    languageCode,
    menuItemNutritionInfo,
    menuItemOptionSetItemNutritionInfos,
    onAddItem,
    restaurantPhoneNumber,
    setShowDialog,
    showDialog,
    showNutritionInfo,
    translate,
  } = props;

  const classes = useStyles();

  const [quantity, setQuantity] = useState(1);

  const { allergens } = useAllergens(
    menuItemNutritionInfo,
    menuItemOptionSetItemNutritionInfos,
  );

  const handleAddItem = (count = 1): void => {
    onAddItem({
      Count: count,
      Item: item,
      Options: [],
    });

    handleCloseDialog();
  };

  const handleCloseDialog = (): void => {
    setShowDialog(false);
  };

  const dialogItem = {
    Item: item,
    Options: [],
  };
  const totalConfigured = getBasketItemTotal(dialogItem) * quantity;
  const totalCurrency = formatNumberToCurrency(
    totalConfigured,
    currency,
    languageCode,
  );

  const renderItemDetails = (): JSX.Element => {
    if (showNutritionInfo) {
      return (
        <MenuItemDetails
          energyContent={menuItemNutritionInfo?.EnergyContent}
          isNutritionInfoError={isNutritionInfoError}
          menuItem={item}
          menuItemAllergens={allergens}
          restaurantPhoneNumber={restaurantPhoneNumber}
          showImageAndName
          translate={translate}
        />
      );
    }

    return (
      <MenuDialogItemDetails
        description={item.Description}
        imageUrl={item.ImageUrl}
        name={item.Name}
      />
    );
  };

  const dialogActions = (
    <MenuDialogAddControls
      itemName={item.Name}
      handleAddItem={(): void => handleAddItem(quantity)}
      quantity={quantity}
      setQuantity={setQuantity}
      totalCurrency={totalCurrency}
      translate={translate}
    />
  );

  return (
    <Dialog
      actions={dialogActions}
      className={classes.dialog}
      dialogName="restaurant-menu-detail"
      onTitleClick={handleCloseDialog}
      open={showDialog}
    >
      {renderItemDetails()}
    </Dialog>
  );
}
